import './Styles.css'
import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useQueryRequest} from './core/QueryRequestProvider'
import {useListView} from './core/ListViewProvider'
import {QueryRequestProvider} from './core/QueryRequestProvider'
import {getStatus} from './core/_requests'
import {QueryResponseProvider, useQueryResponse} from './core/QueryResponseProvider'
import {ID} from '../../../../../../_metronic/helpers'
import {CustomDatePicker} from '../../../../widgets/components/Datepicker'
import {DataTable} from './table/Table'
import {ListGrouping} from './components/header/ListGrouping'
import {TicketProvider} from './core/ListViewProvider'
import {TicketForm} from './form/TicketForm'
import {Tab, Nav, Card, Button} from 'react-bootstrap'
import * as Lucide from 'lucide-react'
import {Plus, X} from 'lucide-react'
import {Data as Ticket} from './core/_models'

interface TicketPageProps {
  externalTicketTrigger?: {phone: string; timestamp: number} | null
  onTriggerProcessed?: () => void
}

type TabType = 'list' | 'add' | 'edit' | 'report'

interface TabItem {
  id: string
  title: string
  isClosable: boolean
  type: TabType
  ticketId?: ID
}
const formatLocal = (d: Date) => {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
const List = ({
  handleAddTab,
  openEditTab,
}: {
  handleAddTab: () => void
  openEditTab: (data: {id: ID; title: string}) => void
}) => {
  const {state, updateState} = useQueryRequest()
  useEffect(() => {
    const currentFilter = (state.filter || {}) as any
    const today = new Date();
    if (!currentFilter.startDate) {
      updateState({
        filter: {
          ...currentFilter,
          startDate: formatLocal(today),
          endDate: formatLocal(today),
        },
        page: 1,
      })
    }
  }, [])
  const [status, setStatus] = useState<Array<{id: string; name: string}>>([])
  const [viewMode, setViewMode] = useState<'table' | 'grid' | 'kanban'>('table')
  const {handleAddTabs, selected} = useListView() as any
  const {response, isLoading} = useQueryResponse()

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const statusResponse = await getStatus()
        setStatus(statusResponse.data)
      } catch (error) {
        console.error('Failed to fetch initial data:', error)
      }
    }
    fetchInitialData()
  }, [])

  const stats = useMemo(() => {
    const r = response as any

    return {
      total: response?.payload?.stats?.total || 0,
      open: response?.payload?.stats?.open || 0,
      closed: response?.payload?.stats?.closed || 0,
      assign: response?.payload?.stats?.assign || 0,
    }
  }, [response])

  const updateFilter = (key: string, value: any) => {
    const currentFilter = (state.filter || {}) as {[key: string]: any}
    updateState({
      filter: { ...currentFilter, [key]: value },
      page: 1,
    })
  }

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    const currentFilter = (state.filter || {}) as {[key: string]: any}
    
    updateState({
      filter: {
        ...currentFilter,
        startDate: start ? formatLocal(start) : undefined,
        endDate: end ? formatLocal(end) : undefined,
      },
      page: 1,
    })
  }

  const handleGlobalSearch = (value: string) => {
    updateState({ ...state, search: value, page: 1 })
  }

  const getDateRangeFromState = () => {
    const filter = state.filter as {[key: string]: any} | undefined
    return {
      startDate: filter?.startDate ? new Date(filter.startDate) : null,
      endDate: filter?.endDate ? new Date(filter.endDate) : null,
    }
  }

  const pillConfigs = [
    { label: 'Total Ticket', value: '', count: stats.total, icon: Lucide.LayoutDashboard, color: '#005AAA' },
    { label: 'Open', value: 'open', count: stats.open, icon: Lucide.CircleDot, color: '#c21a17' },
    { label: 'Closed', value: 'closed', count: stats.closed, icon: Lucide.CheckCircle2, color: '#30e320' },
    { label: 'Assign', value: 'assign', count: stats.assign, icon: Lucide.UserCog, color: '#e8cb0e' },
  ]

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'danger'
      case 'medium': return 'warning'
      case 'low': return 'success'
      default: return 'primary'
    }
  }

  const renderGridView = () => {
    const tickets = response?.data || []
    if (isLoading) return <div className='d-flex justify-content-center p-10'><div className='spinner-border text-primary' /></div>
    if (tickets.length === 0) return <div className='text-center p-10 text-muted'>No tickets found.</div>
    
    return (
      <div className='row g-4'>
        {tickets.map((t: any) => (
          <div key={t.id} className='col-md-6 col-lg-4 col-xl-3'>
            <div className='card h-100 shadow-sm border-0 hover-elevate-up transition-all cursor-pointer' onClick={() => openEditTab({id: t.id, title: t.title || t.id})}>
              <div className='card-body p-5'>
                <div className='d-flex justify-content-between align-items-start mb-3'>
                  <span className={`badge badge-light-${getPriorityColor(t.priority)} fw-bold`}>{t.priority || 'Normal'}</span>
                  <span className='text-muted fs-8'>{t.createdDt?.substring(0, 10)}</span>
                </div>
                <h5 className='fw-bolder text-gray-900 mb-1 fs-5 text-truncate' title={t.title || 'Untitled Ticket'}>
                  {t.title || `Ticket #${t.id}`}
                </h5>
                <div className='text-gray-500 fs-7 mb-4 text-truncate'>{t.clientName || 'Unknown Client'} • {t.phoneNumber || '-'}</div>
                <div className='d-flex align-items-center justify-content-between mt-auto'>
                  <div className='d-flex align-items-center gap-2'>
                    <div className='symbol symbol-30px symbol-circle'>
                      <div className='symbol-label bg-light-primary text-primary fw-bold fs-8'>
                        {t.agentName?.charAt(0) || 'A'}
                      </div>
                    </div>
                    <span className='text-muted fs-8 fw-semibold text-truncate' style={{maxWidth: '100px'}}>{t.agentName || 'Unassigned'}</span>
                  </div>
                  <span className='badge bg-secondary text-dark fw-bold border'>{t.status || 'Open'}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderKanbanView = () => {
    const tickets = response?.data || []
    if (isLoading) return <div className='d-flex justify-content-center p-10'><div className='spinner-border text-primary' /></div>
    
    // Default statuses based on your pillConfigs or generic statuses
    const defaultCols = ['Open', 'Assign', 'Pending', 'Closed']
    
    // Dynamic columns from data or defaultCols
    const allStatuses = Array.from(new Set(tickets.map((t: any) => t.status || 'Open')))
    const colsToUse = allStatuses.length > 0 ? Array.from(new Set([...defaultCols, ...allStatuses])) : defaultCols

    const grouped = colsToUse.reduce((acc: any, col: string) => {
      acc[col] = tickets.filter((t: any) => (t.status || 'Open') === col)
      return acc
    }, {})

    const getColumnColor = (col: string) => {
      const lower = col.toLowerCase();
      if (lower.includes('open')) return '#3b82f6'; // Blue
      if (lower.includes('assign')) return '#f59e0b'; // Amber
      if (lower.includes('pending')) return '#8b5cf6'; // Purple
      if (lower.includes('close')) return '#10b981'; // Green
      return '#64748b'; // Slate
    }

    return (
      <div className='d-flex flex-nowrap overflow-auto pb-4 gap-6' style={{ minHeight: '65vh', padding: '10px 4px' }}>
        {colsToUse.map(col => {
          const colColor = getColumnColor(col);
          return (
          <div key={col} className='d-flex flex-column flex-shrink-0' style={{ width: '340px' }}>
            {/* Column Header */}
            <div className='d-flex justify-content-between align-items-center mb-4 px-2'>
              <div className='d-flex align-items-center gap-2'>
                <span className='rounded-circle' style={{ width: 10, height: 10, backgroundColor: colColor, boxShadow: `0 0 8px ${colColor}80` }}></span>
                <h6 className='m-0 fw-bolder fs-5 text-gray-800' style={{ letterSpacing: '-0.3px' }}>{col}</h6>
              </div>
              <span className='badge fw-boldest' style={{ backgroundColor: `${colColor}15`, color: colColor, fontSize: '11px', padding: '5px 10px', borderRadius: '8px' }}>
                {grouped[col].length}
              </span>
            </div>

            {/* Column Body */}
            <div className='d-flex flex-column gap-4 overflow-auto px-2 pb-4' style={{ flex: 1, maxHeight: 'calc(65vh - 60px)' }}>
              {grouped[col].map((t: any) => {
                const priorityColorClass = getPriorityColor(t.priority);
                let pColor = '#cbd5e1';
                if (priorityColorClass === 'danger') pColor = '#ef4444';
                else if (priorityColorClass === 'warning') pColor = '#f59e0b';
                else if (priorityColorClass === 'success') pColor = '#10b981';
                else if (priorityColorClass === 'primary') pColor = '#3b82f6';
                else if (priorityColorClass === 'info') pColor = '#0ea5e9';

                return (
                <div 
                  key={t.id} 
                  className='card border-0 cursor-pointer' 
                  onClick={() => openEditTab({id: t.id, title: t.title || t.id})}
                  style={{
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02)',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 25px rgba(0,0,0,0.08), 0 4px 10px rgba(0,0,0,0.03)';
                    e.currentTarget.style.border = `1px solid ${colColor}50`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02)';
                    e.currentTarget.style.border = '1px solid rgba(226, 232, 240, 0.8)';
                  }}
                >
                  {/* Left priority accent line */}
                  <div className='position-absolute top-0 bottom-0 start-0' style={{ width: '4px', backgroundColor: pColor }} />
                  
                  <div className='card-body p-5 ps-6'>
                    {/* ID and Date */}
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <span className='fw-bold' style={{ fontSize: '11px', color: '#94a3b8', letterSpacing: '0.5px' }}>#{t.id}</span>
                      <span className='text-muted' style={{ fontSize: '11px' }}>
                        <i className='bi bi-calendar2-event me-1 opacity-50'></i>
                        {t.createdDt?.substring(0, 10)}
                      </span>
                    </div>

                    {/* Title */}
                    <h5 className='fw-bolder text-gray-900 mb-2 lh-sm' style={{ fontSize: '15px', letterSpacing: '-0.2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {t.title || 'Untitled Ticket'}
                    </h5>

                    {/* Client info */}
                    <div className='d-flex align-items-center text-muted mb-4' style={{ fontSize: '12px' }}>
                      <i className='bi bi-buildings me-2 opacity-50' style={{ fontSize: '13px' }}></i>
                      <span className='text-truncate' style={{ maxWidth: '200px' }}>{t.clientName || 'Unknown Client'}</span>
                    </div>

                    {/* Footer: Priority Badge & Agent Avatar */}
                    <div className='d-flex align-items-center justify-content-between pt-3 mt-1' style={{ borderTop: '1px dashed #e2e8f0' }}>
                      <span className={`badge bg-light-${priorityColorClass} text-${priorityColorClass} fw-bold px-3 py-2`} style={{ borderRadius: '8px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t.priority || 'Normal'}
                      </span>
                      
                      <div className='d-flex align-items-center' title={t.agentName || 'Unassigned'}>
                        <div className='symbol symbol-30px symbol-circle' style={{ border: '2px solid #fff', boxShadow: '0 2px 5px rgba(0,0,0,0.08)' }}>
                          {t.agentName ? (
                            <div className='symbol-label fw-bold' style={{ background: `linear-gradient(135deg, ${colColor}, ${colColor}cc)`, color: '#fff', fontSize: '12px' }}>
                              {t.agentName.charAt(0).toUpperCase()}
                            </div>
                          ) : (
                            <div className='symbol-label fw-bold bg-light text-muted' style={{ fontSize: '12px' }}>
                              ?
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )
              })}
              
              {grouped[col].length === 0 && (
                <div className='d-flex flex-column align-items-center justify-content-center p-8 border border-dashed rounded-4' style={{ borderColor: '#cbd5e1', backgroundColor: 'rgba(248, 250, 252, 0.5)' }}>
                  <div className='rounded-circle d-flex align-items-center justify-content-center mb-3' style={{ width: 48, height: 48, backgroundColor: '#f1f5f9' }}>
                    <i className='bi bi-inbox text-muted fs-3'></i>
                  </div>
                  <span className='text-muted fw-semibold fs-7'>No tickets</span>
                </div>
              )}
            </div>
          </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className='ticket-page-wrapper'>
      <div className='glass-card monitoring-header position-relative'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <h1>Ticket Management</h1>
            <p>Monitor and manage customer support tickets efficiently.</p>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <div className='connection-status-indicator connected'>
              <span className='indicator-dot pulse-glow'></span>
              System Online
            </div>
            <button className='btn btn-primary' onClick={handleAddTab}>
              <i className='bi bi-plus-circle me-2'></i>
              Add Ticket
            </button>
          </div>
        </div>
      </div>
      <div className='row g-4 mb-6 px-2'>
        {pillConfigs.map((pill, idx) => {
          const currentStatus = ((state.filter || {}) as any).status
          const isActive = currentStatus === pill.value || (pill.value === '' && !currentStatus)
          const Icon = pill.icon

          return (
            <div key={idx} className='col-md-3'>
              <div 
                onClick={() => updateFilter('status', pill.value || undefined)}
                className={`status-pill-card p-4 d-flex align-items-center cursor-pointer transition-all ${isActive ? 'active' : ''}`}
                style={{ 
                  backgroundColor: isActive ? pill.color : '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: isActive ? `1px solid ${pill.color}` : '1px solid #eee'
                }}
              >
                <div className='rounded-circle d-flex align-items-center justify-content-center me-3'
                  style={{ 
                    width: '40px', height: '40px', 
                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : `${pill.color}10` 
                  }}>
                  {Icon && <Icon size={20} color={isActive ? '#fff' : pill.color} />}
                </div>
                <div>
                  <div className={`fw-bold fs-7 ${isActive ? 'text-white opacity-75' : 'text-muted'}`}>
                    {pill.label}
                  </div>
                  <div className={`fw-bolder fs-4 ${isActive ? 'text-white' : 'text-dark'}`}>
                    {Number(pill.count).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className='glass-card search-filter-container'>
        {selected.length > 0 ? (
          <ListGrouping />
        ) : (
          <>
            <div className='row g-3 mb-4 align-items-center'>
              <div className='col-md-6'>
                <div className='input-group'>
                  <span className='input-group-text bg-white border-end-0'><i className='bi bi-search text-gray-500'></i></span>
                  <input
                    type='text'
                    className='form-control border-start-0 ps-0 modern-search-input'
                    placeholder='Search tickets...'
                    value={state.search || ''}
                    onChange={(e) => handleGlobalSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className='col-md-6 d-flex justify-content-md-end'>
                <div className='btn-group bg-light rounded-3 p-1 border border-gray-200'>
                  <button 
                    className={`btn btn-sm btn-icon border-0 rounded-2 ${viewMode === 'table' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover-bg-light'}`}
                    onClick={() => setViewMode('table')}
                    title="Table View"
                    style={{ width: '36px', height: '36px' }}
                  >
                    <Lucide.Table size={18} />
                  </button>
                  <button 
                    className={`btn btn-sm btn-icon border-0 rounded-2 ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover-bg-light'}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                    style={{ width: '36px', height: '36px' }}
                  >
                    <Lucide.LayoutGrid size={18} />
                  </button>
                  <button 
                    className={`btn btn-sm btn-icon border-0 rounded-2 ${viewMode === 'kanban' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover-bg-light'}`}
                    onClick={() => setViewMode('kanban')}
                    title="Kanban View"
                    style={{ width: '36px', height: '36px' }}
                  >
                    <Lucide.KanbanSquare size={18} />
                  </button>
                </div>
              </div>
            </div>
            
            <div className='row g-3 align-items-end'>
              <div className='col-lg-4 col-md-6'>
                <CustomDatePicker dateRange={getDateRangeFromState()} onDateChange={handleDateChange} />
              </div>
              <div className='col-lg-2 col-md-3 col-sm-6'>
                <select
                  className='form-select'
                  value={((state.filter || {}) as any).status || ''}
                  onChange={(e) => updateFilter('status', e.target.value)}
                >
                  <option value=''>All Statuses</option>
                  {status.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-lg-2 col-md-3 col-sm-6'>
                <select
                  className='form-select'
                  value={state.items_per_page}
                  onChange={(e) => updateState({ items_per_page: Number(e.target.value) as any, page: 1 })}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className='col-lg-4 col-md-12 d-flex gap-2 justify-content-lg-end'>
                <button 
                  className='btn btn-primary flex-grow-1 flex-lg-grow-0' 
                  onClick={() => updateState({...state, page: 1})}
                  title='Apply Filters'
                >
                  <i className='bi bi-funnel-fill d-none d-sm-inline me-1'></i>
                  <span className='d-none d-sm-inline'>Apply</span>
                  <i className='bi bi-funnel-fill d-sm-none'></i>
                </button>
                <button 
                  className='btn btn-warning flex-grow-1 flex-lg-grow-0' 
                  onClick={() => updateState({search: '', filter: {}, page: 1})}
                  title='Clear Filters'
                >
                  <i className='bi bi-x-circle-fill d-none d-sm-inline me-1'></i>
                  <span className='d-none d-sm-inline'>Clear</span>
                  <i className='bi bi-x-circle-fill d-sm-none'></i>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className={`mt-6 ${viewMode === 'table' ? 'glass-card' : ''}`}>
        {viewMode === 'table' && <DataTable />}
        {viewMode === 'grid' && renderGridView()}
        {viewMode === 'kanban' && renderKanbanView()}
      </div>
    </div>
  )
}


const ListWrapper = ({externalTicketTrigger, onTriggerProcessed}: TicketPageProps) => {
  const [openTabs, setOpenTabs] = useState<TabItem[]>([
    {
      id: 'ticket-list',
      title: 'Daftar Tiket',
      isClosable: false,
      type: 'list',
    },
  ])
  const [activeTab, setActiveTab] = useState<string>('ticket-list')
  const handleCloseTab = useCallback((tabId: string) => {
    if (tabId === 'ticket-list') return
    setOpenTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== tabId))
    setActiveTab('ticket-list')
    
    sessionStorage.removeItem(`pop_phone_${tabId}`)
  }, [])

  const getNextAddNumber = (tabs: TabItem[]): number => {
    const addTabNumbers = tabs
      .map((tab) => {
        const match = tab.title.match(/^Untitled-(\d+)$/)
        return match ? parseInt(match[1], 10) : 0
      })
      .filter((num) => num > 0)
      .sort((a, b) => a - b)

    let nextNumber = 1
    for (const num of addTabNumbers) {
      if (nextNumber < num) break
      nextNumber = num + 1
    }
    return nextNumber
  }
  const handleAddTab = useCallback(() => {
    const nextAddNumber = getNextAddNumber(openTabs)
    const newTabId = `ticket-add-${nextAddNumber}`
    const newTab: TabItem = {
      id: newTabId,
      title: `Untitled-${nextAddNumber}`,
      isClosable: true,
      type: 'add',
    }
    setOpenTabs((prevTabs) => [...prevTabs, newTab])
    setActiveTab(newTab.id)
  }, [openTabs])
  
  const handleUpdateSuccess = useCallback((updatedTicket: Ticket) => {
    if (!updatedTicket.id) return
    const tabId = `ticket-edit-${updatedTicket.id}`
    setOpenTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== tabId))
    setActiveTab('ticket-list')
  }, [])

  const handleSaveSuccess = useCallback(
    (addTabId: string, newTicket: Ticket) => {
      if (!newTicket.id) return
      setOpenTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== addTabId))
      setActiveTab('ticket-list')
      sessionStorage.removeItem(`pop_phone_${addTabId}`)
    },
    []
  )

  const openEditTab = useCallback((data: {id: ID; title: string}) => {
    const tabId = `ticket-edit-${data.id}`
    const existingTab = openTabs.find((tab) => tab.id === tabId)
    if (existingTab) {
      setActiveTab(tabId)
      return
    }
    const newTab: TabItem = {
      id: tabId,
      title: `Edit: ${data.title.substring(0, 20)}${data.title.length > 20 ? '...' : ''}`,
      isClosable: true,
      type: 'edit',
      ticketId: data.id,
    }
    setOpenTabs((prevTabs) => [...prevTabs, newTab])
    setActiveTab(tabId)
  }, [openTabs])

  useEffect(() => {
    if (externalTicketTrigger && externalTicketTrigger.phone) {
      const phone = externalTicketTrigger.phone;
      const ts = externalTicketTrigger.timestamp;
      const newTabId = `ticket-add-pop-${ts}`;

      setOpenTabs((prev) => {
        const isExist = prev.find((t) => t.id === newTabId);
        if (isExist) return prev;

        return [
          ...prev,
          {
            id: newTabId,
            title: `Call: ${phone}`,
            isClosable: true,
            type: 'add',
          },
        ];
      });

      setActiveTab(newTabId);
      sessionStorage.setItem(`pop_phone_${newTabId}`, phone);

      if (onTriggerProcessed) {
        onTriggerProcessed();
      }
    }
  }, [externalTicketTrigger, onTriggerProcessed]);

  const renderTabContent = (tab: TabItem) => {
    switch (tab.type) {
      case 'list':
        return <List handleAddTab={handleAddTab} openEditTab={openEditTab} />
      case 'add':
        return (
          <div className='px-8 py-5'>
            <TicketForm
              initialPhoneNumber={sessionStorage.getItem(`pop_phone_${tab.id}`) || ''}
              onSaveSuccess={(newTicket) => handleSaveSuccess(tab.id, newTicket)}
              onCancel={() => handleCloseTab(tab.id)}
            />
          </div>
        )
      case 'edit':
        return (
          <div className='px-8 py-5'>
            <TicketForm
              id={tab.ticketId}
              onSaveSuccess={handleUpdateSuccess}
              onCancel={() => handleCloseTab(tab.id)}
            />
          </div>
        )
      default:
        return null
    }
  }
  

  return (
    <QueryRequestProvider>
      <QueryResponseProvider>
        <TicketProvider 
          handleAddTab={handleAddTab} 
          openEditTab={openEditTab}
        >
          <Card className='mb-5 mb-xl-8'>
            <Tab.Container id='ticket-tab-container' activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
              <Card.Header className='border-0 pt-5'>
                <Nav variant='tabs' className='flex-nowrap overflow-auto'>
                  {openTabs.map((tab) => (
                    <Nav.Item key={tab.id}>
                      <Nav.Link eventKey={tab.id} className='d-flex align-items-center text-nowrap'>
                        {tab.title}
                        {tab.isClosable && (
                          <Button
                            variant='link'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCloseTab(tab.id)
                            }}
                            className='p-0 ms-2 lh-1'
                          >
                            &times;
                          </Button>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                  <Nav.Item className='d-flex align-items-center'>
                    <Button 
                      variant='link' 
                      className='nav-link d-flex align-items-center justify-content-center mb-6 ms-2' 
                      onClick={handleAddTab}
                      style={{
                        width: '35px', 
                        height: '35px', 
                        minWidth: '35px', 
                        borderRadius: '8px',
                      }}
                    >
                      <Plus size={20} strokeWidth={2.5} />
                    </Button>
                  </Nav.Item>
                </Nav>
              </Card.Header>
              <Card.Body className='py-0 px-0'>
                <Tab.Content>
                  {openTabs.map((tab) => (
                    <Tab.Pane key={tab.id} eventKey={tab.id}>
                      {renderTabContent(tab)}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Card.Body>
            </Tab.Container>
          </Card>
        </TicketProvider>
      </QueryResponseProvider>
    </QueryRequestProvider>
  )
}

export {ListWrapper}